import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import GoogleAuth from '../components/GoogleAuth';
import RichTextEditor from '../components/RichTextEditor';
import 'flag-icons/css/flag-icons.min.css';

// Common countries list with their codes and names
const countries = [
  { code: 'W', name: 'World' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'España' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Deutschland' },
  { code: 'IT', name: 'Italia' },
  { code: 'PT', name: 'Portugal' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'BR', name: 'Brasil' },
  { code: 'MX', name: 'México' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CL', name: 'Chile' },
  { code: 'PE', name: 'Perú' },
  { code: 'JP', name: '日本' },
  { code: 'KR', name: '한국' },
  { code: 'CN', name: '中国' },
  { code: 'IN', name: 'India' },
  { code: 'RU', name: 'Россия' },
  { code: 'NL', name: 'Nederland' },
  { code: 'BE', name: 'België' },
  { code: 'CH', name: 'Schweiz' },
  { code: 'AT', name: 'Österreich' },
  { code: 'PH', name: 'Philippines' }
];

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000); // Auto close after 5 seconds
    
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-600' : 'bg-red-600';
  const icon = type === 'success' ? 
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg> :
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>;

  return (
    <div className={`fixed bottom-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 max-w-sm z-50 animate-slide-in`}>
      {icon}
      <span className="text-sm">{message}</span>
      <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const Application = () => {
  const { user, loading, getValidJWTToken } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    profilePhotos: [], // Array to hold multiple photos (max 5)
    vision: '',
    presentation: '',
    youtubeLinks: [''], // Array to hold multiple YouTube links
    email: user?.email || '',
    socialMedia: '',
    followers: '',
    referralCode: '',
    streamer: '',
    url: '', // Add URL field
    profile: 'Streamer', // Add profile type (default: Streamer)
    member_counter: '', // Add member counter field for Discord
    monthly_traffic: '' // Add monthly traffic field for Website
  });
  const [photoPreviews, setPhotoPreviews] = useState([]); // Array for multiple previews
  const [photoUploadStatus, setPhotoUploadStatus] = useState(null); // null, 'success', 'error'
  const [photoUploadMessage, setPhotoUploadMessage] = useState('');
  const [profilePhotoIndex, setProfilePhotoIndex] = useState(0); // Track which photo is marked as profile
  const [logoPhotoIndex, setLogoPhotoIndex] = useState(0); // Track which photo is marked as logo (default 0)
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null); // Track which dropdown is open
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationData, setLocationData] = useState({
    country: 'Detecting location...',
    countryCode: null,
    flagClass: null
  });
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [manualLocationOverride, setManualLocationOverride] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Add success modal state
  const [streamerValidation, setStreamerValidation] = useState({ status: '', message: '' });
  const [emailValidation, setEmailValidation] = useState({ status: '', message: '' });
  const [socialMediaTags, setSocialMediaTags] = useState([]); // Array of {platform: '', username: ''}
  const [socialInput, setSocialInput] = useState('');
  const [showSocialSuggestions, setShowSocialSuggestions] = useState(false);
  const [selectedSocialIndex, setSelectedSocialIndex] = useState(0); // For keyboard navigation

  // Social media platforms configuration
  const socialPlatforms = [
    { name: 'YouTube', icon: '▶️', color: 'bg-red-500', urlPattern: 'https://www.youtube.com/@', useAt: true },
    { name: 'TikTok', icon: '🎵', color: 'bg-black', urlPattern: 'https://www.tiktok.com/@', useAt: true },
    { name: 'Instagram', icon: '📷', color: 'bg-pink-500', urlPattern: 'https://www.instagram.com/', useAt: false },
    { name: 'X', icon: '𝕏', color: 'bg-black', urlPattern: 'https://x.com/', useAt: false },
    { name: 'Facebook', icon: '👤', color: 'bg-blue-600', urlPattern: 'https://www.facebook.com/', useAt: false },
    { name: 'Twitch', icon: '🎮', color: 'bg-purple-500', urlPattern: 'https://www.twitch.tv/', useAt: false },
    { name: 'Discord', icon: '💬', color: 'bg-indigo-500', urlPattern: 'https://discord.com/users/', useAt: false }
  ];

  // Handle social input change
  const handleSocialInputChange = (e) => {
    const value = e.target.value;
    setSocialInput(value);
    setShowSocialSuggestions(value.length > 0);
    setSelectedSocialIndex(0); // Reset selection when typing
  };

  // Handle keyboard navigation for social media dropdown
  const handleSocialKeyDown = (e) => {
    if (!showSocialSuggestions || socialInput.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSocialIndex(prev => 
        prev < socialPlatforms.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSocialIndex(prev => 
        prev > 0 ? prev - 1 : socialPlatforms.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (socialPlatforms[selectedSocialIndex]) {
        addSocialTag(socialPlatforms[selectedSocialIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSocialSuggestions(false);
    }
  };

  // Add social media tag
  const addSocialTag = (platform) => {
    const username = socialInput.replace('@', '').trim();
    if (!username) return;

    const newTag = {
      platform: platform.name,
      username: username,
      icon: platform.icon,
      color: platform.color,
      useAt: platform.useAt
    };

    setSocialMediaTags(prev => [...prev, newTag]);
    setSocialInput('');
    setShowSocialSuggestions(false);

    // Update formData for submission
    updateSocialMediaFormData([...socialMediaTags, newTag]);
  };

  // Remove social media tag
  const removeSocialTag = (index) => {
    const newTags = socialMediaTags.filter((_, i) => i !== index);
    setSocialMediaTags(newTags);
    updateSocialMediaFormData(newTags);
  };

  // Convert tags to format needed for submission
  const updateSocialMediaFormData = (tags) => {
    const socialStrings = tags.map(tag => {
      const platform = socialPlatforms.find(p => p.name === tag.platform);
      return `${platform?.urlPattern}${tag.username}`;
    });
    
    setFormData(prev => ({
      ...prev,
      socialMedia: socialStrings.join(', ')
    }));
  };

  // Get flag CSS class for flag-icons
  const getFlagClass = (countryCode) => {
    if (!countryCode) {
      return null;
    }
    // Handle special case for World option
    if (countryCode === 'W') {
      return 'world-flag'; // Special class for world option
    }
    if (countryCode.length !== 2) {
      return null;
    }
    const code = countryCode.toLowerCase();
    return `fi fi-${code}`;
  };

  // Get country name
  const getCountryName = useCallback((country) => {
    return country.name;
  }, []);

  // Handle manual location selection
  const handleLocationSelect = (country) => {
    const flagClass = getFlagClass(country.code);
    
    setManualLocationOverride(true);
    setShowLocationDropdown(false);
    setSearchQuery('');
    setIsLocationLoading(false);
    
    setLocationData({
      country: getCountryName(country),
      countryCode: country.code,
      flagClass: flagClass
    });
  };

  // Filter countries based on search query
  const filteredCountries = countries.filter(country => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const countryName = country.name.toLowerCase();
    const countryCode = country.code.toLowerCase();
    
    return countryName.includes(query) || countryCode.includes(query);
  });

  // Detect user location on component mount
  useEffect(() => {
    if (manualLocationOverride) return;

    const detectLocation = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          
          const countryCode = data.country_code || 'US';
          const countryName = data.country_name || 'Unknown';
          const flagClass = getFlagClass(countryCode);
          
          setLocationData({
            country: countryName,
            countryCode: countryCode,
            flagClass: flagClass
          });
          
          setIsLocationLoading(false);
        }
      } catch (error) {
        setLocationData({
          country: 'Location unavailable',
          countryCode: null,
          flagClass: null
        });
        setIsLocationLoading(false);
      }
    };

    detectLocation();
  }, [manualLocationOverride]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-dropdown')) {
        setShowLocationDropdown(false);
        setSearchQuery('');
      }
      
      // Close image dropdown when clicking outside
      if (openDropdownIndex !== null && !event.target.closest('.relative.group')) {
        setOpenDropdownIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown, openDropdownIndex]);

  // Update email field when user loads
  useEffect(() => {
    if (user?.email && formData.email !== user.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user, formData.email]);

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-opacity-75 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-black bg-opacity-75 text-white flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              {t('loginToCustomize')}
            </p>
          </div>
          
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              {t('loginRequired')}
            </h2>
            <p className="text-gray-400 text-center mb-6">
              {t('mustBeLoggedIn')}
            </p>
            
            <div className="flex justify-center">
              <GoogleAuth />
            </div>
            
            <p className="text-gray-500 text-sm text-center mt-6">
              {t('byLoggingIn')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    
    if (files.length === 0) return;
    
    // Reset status
    setPhotoUploadStatus(null);
    setPhotoUploadMessage('');
    
    // Check if adding these files would exceed the maximum
    const currentCount = formData.profilePhotos.length;
    const newFilesCount = files.length;
    const totalCount = currentCount + newFilesCount;
    
    if (totalCount > maxFiles) {
      const errorMessage = `You can only upload up to ${maxFiles} images. Currently you have ${currentCount}, trying to add ${newFilesCount}.`;
      setPhotoUploadStatus('error');
      setPhotoUploadMessage(errorMessage);
      setToast({ 
        message: errorMessage, 
        type: 'error' 
      });
      e.target.value = ''; // Clear the input
      return;
    }
    
    const validFiles = [];
    let hasErrors = false;
    
    // Validate each file
    for (let file of files) {
      // Validate file type
      const validImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        setPhotoUploadStatus('error');
        setPhotoUploadMessage(`${file.name}: ${t('invalidImageFormat')}`);
        setToast({ 
          message: `${file.name}: ${t('invalidImageFormat')}`, 
          type: 'error' 
        });
        hasErrors = true;
        break;
      }
      
      // Validate file size (10MB limit per file)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
        const errorMessage = `${file.name}: ${t('fileTooLargeProfile').replace('{size}', sizeInMB)}`;
        setPhotoUploadStatus('error');
        setPhotoUploadMessage(errorMessage);
        setToast({ 
          message: errorMessage, 
          type: 'error' 
        });
        hasErrors = true;
        break;
      }
      
      validFiles.push(file);
    }
    
    if (hasErrors) {
      e.target.value = ''; // Clear the input
      return;
    }
    
    // Create previews for valid files
    let loadedCount = 0;
    const newPreviews = [...photoPreviews];
    const newPhotos = [...formData.profilePhotos];
    
    validFiles.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        newPhotos.push(file);
        loadedCount++;
        
        // Update state when all files are loaded
        if (loadedCount === validFiles.length) {
          setPhotoPreviews(newPreviews);
          setFormData(prev => ({
            ...prev,
            profilePhotos: newPhotos
          }));
          setPhotoUploadStatus('success');
          setPhotoUploadMessage(`${validFiles.length} image${validFiles.length > 1 ? 's' : ''} added (${newPhotos.length}/${maxFiles})`);
        }
      };
      reader.readAsDataURL(file);
    });
    
    // Clear the input for next selection
    e.target.value = '';
  };
  
  // Function to remove a specific image
  const handleRemovePhoto = (indexToRemove) => {
    const newPhotos = formData.profilePhotos.filter((_, index) => index !== indexToRemove);
    const newPreviews = photoPreviews.filter((_, index) => index !== indexToRemove);
    
    setFormData(prev => ({
      ...prev,
      profilePhotos: newPhotos
    }));
    setPhotoPreviews(newPreviews);
    
    // Adjust profilePhotoIndex if needed
    if (indexToRemove === profilePhotoIndex) {
      // If removing the marked profile photo, reset to index 0
      setProfilePhotoIndex(0);
    } else if (indexToRemove < profilePhotoIndex) {
      // If removing a photo before the profile photo, shift index down
      setProfilePhotoIndex(profilePhotoIndex - 1);
    }
    
    // Adjust logoPhotoIndex if needed
    if (indexToRemove === logoPhotoIndex) {
      // If removing the marked logo photo, reset to index 0
      setLogoPhotoIndex(0);
    } else if (indexToRemove < logoPhotoIndex) {
      // If removing a photo before the logo photo, shift index down
      setLogoPhotoIndex(logoPhotoIndex - 1);
    }
    
    if (newPhotos.length > 0) {
      setPhotoUploadMessage(`${newPhotos.length}/5 images selected`);
    } else {
      setPhotoUploadStatus(null);
      setPhotoUploadMessage('');
    }
  };

  // Function to mark a specific image as profile picture (doesn't reorder, just marks)
  const handleSetAsProfile = (index) => {
    setProfilePhotoIndex(index);
    setOpenDropdownIndex(null); // Close dropdown after selection
  };

  // Function to mark a specific image as logo
  const handleSetAsLogo = (index) => {
    setLogoPhotoIndex(index);
    setOpenDropdownIndex(null); // Close dropdown after selection
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation errors when user starts typing
    if (name === 'streamer' && streamerValidation.status === 'error') {
      setStreamerValidation({ status: '', message: '' });
    }
    if (name === 'email' && emailValidation.status === 'error') {
      setEmailValidation({ status: '', message: '' });
    }
  };

  // Handle YouTube link changes
  const handleYoutubeChange = (index, value) => {
    setFormData(prev => {
      const newYoutubeLinks = [...prev.youtubeLinks];
      newYoutubeLinks[index] = value;
      return { ...prev, youtubeLinks: newYoutubeLinks };
    });
  };

  // Add new YouTube link input
  const addYoutubeLink = () => {
    setFormData(prev => ({
      ...prev,
      youtubeLinks: [...prev.youtubeLinks, '']
    }));
  };

  // Remove YouTube link input
  const removeYoutubeLink = (index) => {
    if (formData.youtubeLinks.length > 1) {
      setFormData(prev => ({
        ...prev,
        youtubeLinks: prev.youtubeLinks.filter((_, i) => i !== index)
      }));
    }
  };

  const handlePublish = async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!formData.profilePhotos || formData.profilePhotos.length === 0) {
        setToast({ message: 'Please add at least one profile photo', type: 'error' });
        return;
      }

      // Validate each photo
      for (let i = 0; i < formData.profilePhotos.length; i++) {
        const photo = formData.profilePhotos[i];
        
        // Validate file type (allow PNG, JPEG, JPG, WebP)
        const validImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        if (!validImageTypes.includes(photo.type)) {
          setToast({ 
            message: `Photo ${i + 1}: ${t('invalidImageFormat')}`, 
            type: 'error' 
          });
          return;
        }

        // Validate file size (5MB limit for profile photos)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (photo.size > maxSize) {
          const sizeInMB = (photo.size / (1024 * 1024)).toFixed(1);
          setToast({ 
            message: `Photo ${i + 1}: ${t('fileTooLargeProfile').replace('{size}', sizeInMB)}`, 
            type: 'error' 
          });
          return;
        }
      }
      
      if (!formData.vision.trim()) {
        setToast({ message: t('pleaseWriteVision'), type: 'error' });
        return;
      }
      
      if (!formData.presentation.trim()) {
        setToast({ message: t('pleaseWritePresentation'), type: 'error' });
        return;
      }
      
      if (!formData.socialMedia.trim()) {
        setToast({ message: t('pleaseIndicateSocialMedia'), type: 'error' });
        return;
      }
      
      // Followers is now optional, removed validation
      
      if (!formData.streamer.trim()) {
        setToast({ message: t('pleaseIndicateStreamerName'), type: 'error' });
        return;
      }

      // Clear previous validation errors before submission
      setStreamerValidation({ status: '', message: '' });
      setEmailValidation({ status: '', message: '' });


      // Get authentication header with automatic token refresh
      let authHeader = '';
      try {
        const authToken = await getValidJWTToken();
        
        if (authToken) {
          authHeader = `Bearer ${authToken}`;
        } else {
          setToast({ 
            message: t('sessionExpiredLogin'), 
            type: 'info' 
          });
          return;
        }
      } catch (error) {
        console.error('❌ Error getting auth token:', error);
        setToast({ 
          message: t('authenticationErrorOccurred'), 
          type: 'error' 
        });
        return;
      }
      
      // Step 1: Upload multiple images directly to Cloudinary
      let imageData = { profile: [], logo: [] };
      let publicIdData = { profile: [], logo: [] };
      
      try {
        // Get Cloudinary signature
        const signatureResponse = await fetch('/.netlify/functions/get-cloudinary-signature', {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projectName: formData.streamer // Pass project name to create nested folder
          })
        });
        
        if (!signatureResponse.ok) {
          throw new Error('Failed to get Cloudinary signature');
        }
        
        const signatureData = await signatureResponse.json();
        
        // Upload each image to Cloudinary maintaining original order
        const uploadedImages = []; // Store {url, publicId, originalIndex}
        
        for (let i = 0; i < formData.profilePhotos.length; i++) {
          const photo = formData.profilePhotos[i];
          
          // Prepare image upload to Cloudinary
          const cloudinaryFormData = new FormData();
          cloudinaryFormData.append('file', photo);
          cloudinaryFormData.append('api_key', signatureData.apiKey);
          cloudinaryFormData.append('timestamp', signatureData.timestamp);
          cloudinaryFormData.append('signature', signatureData.signature);
          cloudinaryFormData.append('folder', signatureData.folder);
          
          // Upload directly to Cloudinary
          const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`, {
            method: 'POST',
            body: cloudinaryFormData
          });
          
          if (!cloudinaryResponse.ok) {
            const errorText = await cloudinaryResponse.text();
            throw new Error(`Cloudinary upload failed for image ${i + 1}: ${errorText}`);
          }
          
          const cloudinaryResult = await cloudinaryResponse.json();
          uploadedImages.push({
            url: cloudinaryResult.secure_url,
            publicId: cloudinaryResult.public_id,
            originalIndex: i
          });
          
        }
        
        
        // Organize images into profile and logo arrays
        const logoImages = [];
        const profileImages = [];
        const profilePublicIds = [];
        
        // First, add the chosen profile image at index 0
        const profileImage = uploadedImages.find(img => img.originalIndex === profilePhotoIndex);
        if (profileImage) {
          profileImages.push(profileImage.url);
          profilePublicIds.push(profileImage.publicId);
        }
        
        // Then add all other images (excluding the profile image we just added)
        uploadedImages.forEach(img => {
          if (img.originalIndex !== profilePhotoIndex) {
            profileImages.push(img.url);
            profilePublicIds.push(img.publicId);
          }
        });
        
        // Add only the chosen logo image to logo array
        const logoImage = uploadedImages.find(img => img.originalIndex === logoPhotoIndex);
        if (logoImage) {
          logoImages.push(logoImage.url);
        }
        
        // Organize logo publicIds
        const logoPublicIds = [];
        if (logoImage) {
          logoPublicIds.push(logoImage.publicId);
        }
        
        // Update the structured image object
        imageData = {
          profile: profileImages,
          logo: logoImages
        };
        
        publicIdData = {
          profile: profilePublicIds,
          logo: logoPublicIds
        };
        
        
      } catch (cloudinaryError) {
        console.error('❌ Cloudinary upload failed:', cloudinaryError);
        setToast({ 
          message: `Image upload failed: ${cloudinaryError.message}`, 
          type: 'error' 
        });
        return;
      }
      
      // Step 2: Submit application data (without image file)
      
      // Filter out empty YouTube links and prepare JSON array
      const validYoutubeLinks = formData.youtubeLinks.filter(link => link.trim() !== '');
      
      // Clean HTML content by removing leading <br> tags, empty elements, and extra <br> tags
      // ✅ EMOJI SUPPORT: This function preserves all emojis and UTF-8 characters
      const cleanHtmlContent = (html) => {
        if (!html) return '';
        
        let cleaned = html;
        
        // Remove leading <br> tags, empty paragraphs, divs, spans
        // ⚠️ Note: These regex patterns ONLY target HTML tags, NOT emojis or UTF-8 characters
        cleaned = cleaned
          .replace(/^(\s*<br\s*\/?>\s*)+/gi, '') // Remove leading br tags
          .replace(/^(\s*<p[^>]*>\s*<\/p>\s*)+/gi, '') // Remove empty paragraphs at start
          .replace(/^(\s*<div[^>]*>\s*<\/div>\s*)+/gi, '') // Remove empty divs at start
          .replace(/^(\s*<span[^>]*>\s*<\/span>\s*)+/gi, '') // Remove empty spans at start
          .replace(/^(\s*&nbsp;\s*)+/gi, '') // Remove leading non-breaking spaces
          .replace(/(\s*<br\s*\/?>\s*){2,}/gi, '<br>') // Replace 2+ consecutive br tags with just 1
          .replace(/(\s*<br\s*\/?>\s*)+$/gi, '') // Remove trailing br tags
          .trim(); // Remove any remaining leading/trailing whitespace
        
        // ✅ Emojis and all UTF-8 characters are preserved and saved to Supabase as-is
        return cleaned;
      };
      
      // Convert social media string to array (already formatted URLs from tags)
      const socialArray = formData.socialMedia 
        ? formData.socialMedia.split(', ').filter(url => url.trim())
        : [];
      
      const applicationData = {
        name: user?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || 'Unknown User',
        email: formData.email,
        country: locationData.country,
        message: cleanHtmlContent(formData.vision),
        about: cleanHtmlContent(formData.presentation),
        social: JSON.stringify(socialArray),
        subscriber: formData.followers && formData.followers.trim() !== '' 
          ? (typeof formData.followers === 'string' ? parseInt(formData.followers, 10) : formData.followers)
          : null, // Convert to integer or null
        referral: formData.referralCode || '',
        streamer: formData.streamer,
        youtube: JSON.stringify(validYoutubeLinks),
        url: formData.url || '', // Add URL field
        profile: formData.profile, // Add profile type
        member_counter: formData.profile === 'Discord' && formData.member_counter && formData.member_counter.trim() !== '' 
          ? parseInt(formData.member_counter, 10) 
          : null, // Only for Discord, convert to number or null
        monthly_traffic: formData.profile === 'Website' && formData.monthly_traffic && formData.monthly_traffic.trim() !== '' 
          ? parseInt(formData.monthly_traffic, 10) 
          : null, // Only for Website, convert to number or null
        imageUrl: JSON.stringify(imageData),  // Organized object: {profile: [...], logo: [...]}
        publicId: JSON.stringify(publicIdData)   // Organized object: {profile: [...], logo: [...]}
      };
      
      
      const response = await fetch('/.netlify/functions/application-submit', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(applicationData)
      });
       
       
       // Handle response more carefully
       let result;
       const responseText = await response.text();
       
       try {
         result = JSON.parse(responseText);
       } catch (jsonError) {
         console.error('❌ JSON parsing failed:', jsonError.message);
         console.error('📄 Full response text:', responseText);
         throw new Error(`Server returned non-JSON response: ${responseText.substring(0, 100)}`);
       }
       
       if (response.ok && result.success) {
         // Show success modal instead of toast
         setShowSuccessModal(true);
        
        // Reset form after successful submission
        setFormData({
          profilePhotos: [],
          vision: '',
          presentation: '',
          youtubeLinks: [''],
          email: user?.email || '',
          socialMedia: '',
          followers: '',
          referralCode: '',
          streamer: '',
          url: '', // Add URL field to reset
          profile: 'Streamer', // Reset profile type to default
          member_counter: '', // Reset member counter
          monthly_traffic: '' // Reset monthly traffic
        });
        setPhotoPreviews([]);
       } else {
         console.error('❌ Submission failed:', result);
         
         // Check if server returned validation errors
         if (result.validationErrors) {
           console.log('📋 Validation errors received from server:', result.validationErrors);
           
           // Display streamer name error if present
           if (result.validationErrors.streamer) {
             setStreamerValidation({
               status: 'error',
               message: t('language') === 'fr' 
                 ? '❌ Ce nom existe déjà. Veuillez changer le nom du projet.'
                 : `❌ ${result.validationErrors.streamer.message}`
             });
           }
           
           // Display email error if present
           if (result.validationErrors.email) {
             setEmailValidation({
               status: 'error',
               message: t('language') === 'fr' 
                 ? '❌ Cet email existe déjà. Veuillez utiliser un autre email.'
                 : `❌ ${result.validationErrors.email.message}`
             });
           }
           
           // Scroll to the first error field
           const emailField = document.querySelector('input[name="email"]');
           const streamerField = document.querySelector('input[name="streamer"]');
           
           if (result.validationErrors.email && emailField) {
             emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
           } else if (result.validationErrors.streamer && streamerField) {
             streamerField.scrollIntoView({ behavior: 'smooth', block: 'center' });
             streamerField.focus();
           }
           
           setToast({ 
             message: t('language') === 'fr' 
               ? '⚠️ Veuillez corriger les erreurs avant de publier'
               : '⚠️ Please correct the errors before publishing',
             type: 'error' 
           });
         } else {
           // Generic error message if no validation errors
           setToast({ message: result.message || t('errorDuringSubmission'), type: 'error' });
         }
       }
      
    } catch (error) {
      console.error('💥 Error publishing application:', error);
      setToast({ message: t('errorOccurred'), type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black bg-opacity-75 text-white">
      {/* Main Content */}
      <div className="container mx-auto px-6 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 italic" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>
            {t('welcomeDropZone')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">{t('dropZoneExclamation')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('createYourSpace')}
          </p>
          
          {/* Profile Type Selector */}
          <div className="mt-12 max-w-2xl mx-auto">
            <h2 className="text-xl font-semibold text-white mb-6 text-center">{t('whatTypeCommunity')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['Streamer', 'Influencer', 'Discord', 'Website'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, profile: type }))}
                  className={`px-6 py-4 rounded-lg font-semibold transition-all duration-200 ${
                    formData.profile === type
                      ? 'bg-pink-600 text-white shadow-lg transform scale-105'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {t(`profile${type}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Profile Photo Upload */}
          <div className="flex items-start gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center border-2 border-gray-700">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-cyan-400 mb-2">📸 {t('addProfilePhotoAndVisuals')}</h3>
              <div className="space-y-2 text-sm text-gray-400 mb-4">
                <p>• {t('clickUploadDragDrop')}</p>
                <p>• {t('suggestVisualsDropZone')}</p>
                <p>• Click the arrow button on any image to set it as Profile or Logo</p>
              </div>
              <div className="relative">
                <input
                  type="file"
                  id="profilePhotos"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handlePhotoUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  multiple
                />
                
                {/* Format info and upload status for profile photo */}
                <div className="mt-2 space-y-2">
                  <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-xs text-yellow-400 flex items-center gap-2">
                      <span>{t('fileSizeNote')}</span>
                    </p>
                  </div>
                  <p className="text-xs text-gray-400">
                    {t('acceptedFormatsPhoto')}
                  </p>
                  
                  {/* Upload status message */}
                  {photoUploadStatus && (
                    <div className={`text-sm font-medium ${
                      photoUploadStatus === 'success' 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {photoUploadMessage}
                    </div>
                  )}
                </div>
                <div className="bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-cyan-400 transition-colors">
                  {photoPreviews && photoPreviews.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {photoPreviews.map((preview, index) => {
                          const isProfile = index === profilePhotoIndex;
                          const isLogo = index === logoPhotoIndex;
                          const isBoth = isProfile && isLogo;
                          
                          // Determine border color
                          let borderClass = 'border-2 border-transparent';
                          let shadowClass = '';
                          if (isBoth) {
                            borderClass = 'border-4 border-cyan-500';
                            shadowClass = 'shadow-lg shadow-cyan-500/50';
                          } else if (isProfile) {
                            borderClass = 'border-4 border-blue-500';
                            shadowClass = 'shadow-lg shadow-blue-500/50';
                          } else if (isLogo) {
                            borderClass = 'border-4 border-green-500';
                            shadowClass = 'shadow-lg shadow-green-500/50';
                          }
                          
                          return (
                          <div key={index} className="relative group">
                            {/* Image with borders for profile/logo */}
                            <img 
                              src={preview} 
                              alt={`Preview ${index + 1}`} 
                              className={`w-full h-24 object-cover rounded-lg transition-all ${borderClass} ${shadowClass}`}
                            />
                            
                            {/* Badges - shown when NOT hovering */}
                            {isBoth ? (
                              // Both Profile and Logo badge
                              <div className="absolute top-1 left-1 flex flex-col gap-1 opacity-100 group-hover:opacity-0 transition-opacity">
                                <div className="bg-cyan-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg">
                                  Profile
                                </div>
                                <div className="bg-cyan-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg">
                                  Logo
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* Profile Badge only */}
                                {isProfile && (
                                  <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg opacity-100 group-hover:opacity-0 transition-opacity">
                                    Profile
                                  </div>
                                )}
                                
                                {/* Logo Badge only */}
                                {isLogo && (
                                  <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg opacity-100 group-hover:opacity-0 transition-opacity">
                                    Logo
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Dropdown Button in top left - shown on hover */}
                            <div className="absolute top-[-8px] left-[-8px]">
                              <button
                                type="button"
                                onClick={() => setOpenDropdownIndex(openDropdownIndex === index ? null : index)}
                                className="bg-gray-900 hover:bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg border border-gray-600"
                                title="Options"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              
                              {/* Dropdown Menu */}
                              {openDropdownIndex === index && (
                                <div className="absolute top-full left-0 mt-1 bg-gray-800 rounded-lg shadow-xl border border-gray-600 overflow-hidden z-10 min-w-[140px]">
                                  <button
                                    type="button"
                                    onClick={() => handleSetAsProfile(index)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                                      index === profilePhotoIndex ? 'bg-blue-500/20 text-blue-400' : 'text-white'
                                    }`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Set as Profile
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSetAsLogo(index)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                                      index === logoPhotoIndex ? 'bg-purple-500/20 text-purple-400' : 'text-white'
                                    }`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                    </svg>
                                    Set as Logo
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => handleRemovePhoto(index)}
                              className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                              title={`Remove image ${index + 1}`}
                            >
                              ×
                            </button>
                          </div>
                          );
                        })}
                      </div>
                      <p className="text-sm text-gray-300">
                        {photoPreviews.length}/5 images selected
                        {photoPreviews.length < 5 && " • Click to add more"}
                      </p>
                    </div>
                  ) : (
                    <div className="text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>{t('clickHereUpload')}</p>
                      <p className="text-xs mt-1">PNG, JPG, WebP (max 5 images)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="flex items-start gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center border-2 border-gray-700">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-blue-400 mb-2">⚡ {t('writeVision')}</h3>
              <div className="space-y-2 text-sm text-gray-400 mb-4">
                <p>• {t('explainWhatShare')}</p>
                <p>• {t('highlightValues')}</p>
              </div>
              <RichTextEditor
                name="vision"
                value={formData.vision}
                onChange={handleInputChange}
                placeholder={t('writeVisionHere')}
                className="focus:border-blue-400"
              />
            </div>
          </div>

          {/* Presentation */}
          <div className="flex items-start gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center border-2 border-gray-700">
                <svg className="w-8 h-8 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-pink-400 mb-2">💭 {t('introduceYourself')}</h3>
              <div className="space-y-2 text-sm text-gray-400 mb-4">
                <p>• {t('talkAboutContent')}</p>
                <p>• {t('fewLinesEnough')}</p>
              </div>
              <RichTextEditor
                name="presentation"
                value={formData.presentation}
                onChange={handleInputChange}
                placeholder={t('writeIntroHere')}
                className="focus:border-pink-400"
              />
            </div>
          </div>

          {/* YouTube Videos */}
          <div className="flex items-start gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center border-2 border-gray-700">
                <svg className="w-8 h-8 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-yellow-400 mb-2">▶️ {t('showcaseContent')}</h3>
              <div className="space-y-2 text-sm text-gray-400 mb-4">
                <p>{t('addOneMoreVideos')}</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-300">
                    {t('youtubeLinks')}
                  </label>
                  <button
                    type="button"
                    onClick={addYoutubeLink}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('addLink')}
                  </button>
                </div>
                
                {formData.youtubeLinks.map((link, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => handleYoutubeChange(index, e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="flex-1 bg-gray-800 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:outline-none"
                    />
                    {formData.youtubeLinks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeYoutubeLink(index)}
                        className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-lg transition-colors"
                        title={t('removeThisLink')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

           {/* Location and Profile Details */}
           
             <div className="flex items-start gap-8">
               <div className="flex-1">
                 <h3 className="text-xl text-center font-bold text-green-400 mb-4">🌍 {t('completeProfile')}</h3>
                 
                 {/* Location Display */}
                 <div className="mb-6">
                   <div className="relative location-dropdown inline-block w-full text-center">
                     <button
                       onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                       className="inline-flex items-center bg-gray-800 hover:bg-gray-700 rounded-lg px-4 py-3 transition-colors cursor-pointer border border-gray-600"
                     >
                       <div className="flex items-center mr-3">
                         {isLocationLoading ? (
                           <div className="animate-pulse bg-gray-600 rounded w-8 h-6" />
                         ) : locationData.flagClass ? (
                           <span 
                             className={locationData.countryCode === 'W' ? '' : locationData.flagClass}
                             style={{
                               fontSize: locationData.countryCode === 'W' ? '18px' : '20px',
                               width: '28px',
                               height: '20px',
                               borderRadius: '4px',
                               display: 'inline-flex',
                               alignItems: 'center',
                               justifyContent: 'center'
                             }}
                             role="img" 
                             aria-label={`${locationData.country} flag`}
                           >
                             {locationData.countryCode === 'W' ? '🌍' : ''}
                           </span>
                         ) : (
                           <span className="text-green-400 text-sm font-bold bg-green-900 px-2 py-1 rounded">
                             {locationData.countryCode || '?'}
                           </span>
                         )}
                       </div>
                       <span className="text-white text-sm">
                         <strong>{locationData.country}</strong>
                       </span>
                       {!isLocationLoading && locationData.countryCode && (
                         <span className="text-xs text-gray-400 ml-2">({locationData.countryCode})</span>
                       )}
                       <svg 
                         className={`ml-2 h-4 w-4 text-gray-400 transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`}
                         fill="none" 
                         stroke="currentColor" 
                         viewBox="0 0 24 24"
                       >
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                       </svg>
                     </button>

                     {/* Dropdown Menu */}
                     {showLocationDropdown && (
                       <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-gray-800 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto border border-gray-600">
                         <div className="p-3">
                           <div className="text-sm text-gray-400 px-3 py-2 font-medium">
                             {t('selectLocation')}
                           </div>
                           <input
                             type="text"
                             value={searchQuery}
                             onChange={(e) => setSearchQuery(e.target.value)}
                             placeholder={t('searchCountry')}
                             className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent mb-2"
                           />
                           {filteredCountries.length === 0 ? (
                             <div className="text-center py-4 text-gray-400">
                               {t('noCountryFound')}
                             </div>
                           ) : (
                             filteredCountries.map((country) => (
                               <button
                                 key={country.code}
                                 onClick={() => handleLocationSelect(country)}
                                 className={`w-full flex items-center px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors ${
                                   locationData.countryCode === country.code ? 'bg-gray-700' : ''
                                 }`}
                               >
                                <span 
                                  className={country.code === 'W' ? 'mr-3' : `fi fi-${country.code.toLowerCase()} mr-3`}
                                  style={{
                                    fontSize: country.code === 'W' ? '16px' : '16px',
                                    width: '24px',
                                    height: '16px',
                                    borderRadius: '3px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  role="img" 
                                  aria-label={`${country.name} flag`}
                                >
                                  {country.code === 'W' ? '🌍' : ''}
                                </span>
                                 <span className="text-white text-sm">
                                   {country.name}
                                 </span>
                                 <span className="text-xs text-gray-400 ml-auto">
                                   {country.code}
                                 </span>
                               </button>
                             ))
                           )}
                         </div>
                       </div>
                     )}
                   </div>
                 </div>

                 {/* Form Fields */}
                 <div className="space-y-4">
                   {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-green-400 mb-2">
                      {t('emailAddress')}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('enterEmailAddress')}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-300 placeholder-gray-400 cursor-not-allowed"
                        disabled
                        required
                      />
                    </div>
                    
                    {/* Email validation status */}
                    <div className="mt-1">
                      {emailValidation.status === 'success' && (
                        <p className="text-sm text-green-400 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {emailValidation.message}
                        </p>
                      )}
                      {emailValidation.status === 'error' && (
                        <p className="text-sm text-red-400 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          {emailValidation.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        {emailValidation.status ? '' : t('emailAutoFilled')}
                        {emailValidation.status === '' && formData.email && (
                          <span className="ml-2">
                            {t('language') === 'fr' 
                              ? '• Sera vérifié avant soumission'
                              : '• Will be validated before submission'
                            }
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                   {/* Conditional URL/Discord Field */}
                   {formData.profile !== 'Streamer' && formData.profile !== 'Influencer' && (
                     <div>
                       <label className="block text-sm font-medium text-green-400 mb-2">
                         {formData.profile === 'Discord' ? t('discordLink') : t('url')}
                       </label>
                       <input
                         type="url"
                         name="url"
                         value={formData.url}
                         onChange={handleInputChange}
                         placeholder={formData.profile === 'Discord' ? t('discordLinkPlaceholder') : t('urlPlaceholder')}
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                       />
                     </div>
                   )}

                   {/* Monthly Traffic for Website */}
                   {formData.profile === 'Website' && (
                     <div>
                       <label className="block text-sm font-medium text-green-400 mb-2">
                         {t('monthlyTraffic')} <span className="text-gray-400 text-xs ml-1">{t('optional')}</span>
                       </label>
                       <input
                         type="text"
                         name="monthly_traffic"
                         value={formData.monthly_traffic}
                         onChange={handleInputChange}
                         placeholder={t('enterMonthlyTraffic')}
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                       />
                     </div>
                   )}

                   {/* Member Counter for Discord */}
                   {formData.profile === 'Discord' && (
                     <div>
                       <label className="block text-sm font-medium text-green-400 mb-2">
                         {t('memberCounter')} <span className="text-gray-400 text-xs ml-1">{t('optional')}</span>
                       </label>
                       <input
                         type="text"
                         name="member_counter"
                         value={formData.member_counter}
                         onChange={handleInputChange}
                         placeholder={t('memberCounterPlaceholder')}
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                       />
                     </div>
                   )}

                   {/* Social Media with Autocomplete */}
                   <div>
                     <label className="block text-sm font-medium text-green-400 mb-2">
                       {t('mainSocialNetwork')}
                     </label>
                     
                     {/* Input field with tags inside */}
                     <div className="relative">
                       <div className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 min-h-[48px] flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent">
                         {/* Display existing tags inside input */}
                         {socialMediaTags.map((tag, index) => (
                           <div 
                             key={index}
                             className={`${tag.color} text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm`}
                           >
                             <span className="font-medium">{tag.platform} :</span>
                             <span className="font-semibold">{tag.useAt ? '@' : ''}{tag.username}</span>
                             <button
                               type="button"
                               onClick={() => removeSocialTag(index)}
                               className="ml-1 hover:bg-white/20 rounded-full w-4 h-4 flex items-center justify-center text-sm"
                             >
                               ×
                             </button>
                           </div>
                         ))}
                         
                         {/* Input field */}
                         <input
                           type="text"
                           value={socialInput}
                           onChange={handleSocialInputChange}
                           onKeyDown={handleSocialKeyDown}
                           onFocus={() => socialInput.length > 0 && setShowSocialSuggestions(true)}
                           placeholder={socialMediaTags.length === 0 ? "@username" : ""}
                           className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-400 min-w-[200px]"
                         />
                       </div>
                       
                       {/* Platform suggestions dropdown */}
                       {showSocialSuggestions && socialInput.length > 0 && (
                         <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                           <div className="p-2">
                             <p className="text-xs text-gray-400 px-2 py-1">Select platform for @{socialInput.replace('@', '')}</p>
                             {socialPlatforms.map((platform, index) => (
                               <button
                                 key={index}
                                 type="button"
                                 onClick={() => addSocialTag(platform)}
                                 className={`w-full flex items-center gap-3 px-3 py-2 rounded text-left transition-colors ${
                                   index === selectedSocialIndex ? 'bg-gray-700' : 'hover:bg-gray-700'
                                 }`}
                               >
                                 <span className="text-2xl w-8 flex items-center justify-center">{platform.icon}</span>
                                 <div className="flex-1">
                                   <div className="text-white font-medium">{platform.name}</div>
                                   <div className="text-xs text-gray-400">{platform.urlPattern}{socialInput.replace('@', '')}</div>
                                 </div>
                               </button>
                             ))}
                           </div>
                         </div>
                       )}
                     </div>
                     
                     <p className="text-xs text-gray-400 mt-2">
                       Type username then select platform from dropdown
                     </p>
                   </div>

                   {/* Followers/Members - Conditional Label (NOT for Website) */}
                   {formData.profile !== 'Website' && (
                     <div>
                       <label className="block text-sm font-medium text-green-400 mb-2">
                         {formData.profile === 'Discord' ? t('numberOfMembers') : t('subscriberCount')}
                         {/* Only show optional for Discord */}
                         {formData.profile === 'Discord' && (
                           <span className="text-gray-400 text-xs ml-1">{t('optional')}</span>
                         )}
                       </label>
                       <input
                         type="number"
                         name="followers"
                         value={formData.followers}
                         onChange={handleInputChange}
                         placeholder={formData.profile === 'Discord' ? t('enterMemberCount') : t('enterSubscriberCount')}
                         min="0"
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                       />
                     </div>
                   )}

                   {/* Referral Code */}
                   <div>
                     <label className="block text-sm font-medium text-green-400 mb-2">
                       {t('referralCodeOptional')}
                     </label>
                     <input
                       type="text"
                       name="referralCode"
                       value={formData.referralCode}
                       onChange={handleInputChange}
                       placeholder={t('optionalReferralCode')}
                       className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                     />
                   </div>

                   {/* Streamer Name */}
                   <div>
                     <label className="block text-sm font-medium text-green-400 mb-2">
                       {t('creatorProjectName')}
                     </label>
                     <div className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          name="streamer"
                          value={formData.streamer}
                          onChange={handleInputChange}
                          placeholder={t('creatorNamePlaceholder')}
                          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      {/* Validation status */}
                      <div className="mt-2">
                        {streamerValidation.status === 'success' && (
                          <p className="text-sm text-green-400 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {streamerValidation.message}
                          </p>
                        )}
                        {streamerValidation.status === 'error' && (
                          <p className="text-sm text-red-400 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {streamerValidation.message}
                          </p>
                        )}
                        {!streamerValidation.status && (
                          <p className="text-xs text-gray-400">
                            {t('language') === 'fr' 
                              ? 'Ce nom sera vérifié dans les applications et comptes admin existants'
                              : 'This name will be checked against existing applications and admin accounts'
                            }
                          </p>
                        )}
                      </div>
                     </div>
                     <p className="text-xs text-gray-400 mt-1">
                       {t('nameWillIdentifyPage')}
                     </p>
                   </div>
                 </div>
               </div>
             </div>
           



          {/* Publish Section */}
          <div className="text-center bg-gray-900 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-2">
              {t('publicationSubjectVerification')}
            </h3>
            <p className="text-gray-400 mb-6">
               {t('whenReadyClickPublish')} <span className="font-bold text-yellow-400">{t('publishMyDropZone')}</span>.
            </p>
            <p className="text-gray-400 mb-6">
              {t('pageVerifiedByTeam')} <span className="font-bold">{t('notInstantaneous')}</span>.
            </p>
            <button
              onClick={handlePublish}
              disabled={isSubmitting}
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg transition-colors duration-300 flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{isSubmitting ? t('publishingInProgress') : t('publishMyDropZone')}</span>
              {isSubmitting ? (
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
            <p className="text-gray-500 text-xs mt-2">
              {t('noStressModifyLater')}
            </p>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full border border-green-500 shadow-2xl">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-500 bg-opacity-20 mb-4">
                <svg className="h-10 w-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {/* Success Message */}
              <h3 className="text-2xl font-bold text-white mb-2">
                {t('dropZoneCreated')}
              </h3>
              <p className="text-gray-400 text-base mb-6">
                {t('checkSpamFolder')}
              </p>
              
              {/* Close Button */}
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Application;